/**
 * Menu Engineering Page
 * Analyzes menu items using the Menu Engineering Matrix (Stars, Plowhorses, Puzzles, Dogs)
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { advancedAnalyticsApi } from '../lib/api/analytics';
import { MenuEngineering, MenuItemAnalysis, MenuCategory, MENU_CATEGORY_LABELS } from '../types/analytics';
import { MenuEngineeringMatrix } from '../components/analytics/MenuEngineeringMatrix';
import { MetricCard } from '../components/analytics/MetricCard';

export default function MenuEngineeringPage() {
  const [loading, setLoading] = useState(true);
  const [menuData, setMenuData] = useState<MenuEngineering | null>(null);
  const [dateRange, setDateRange] = useState<'3m' | '6m' | '1y' | 'all'>('6m');
  const [selectedCategory, setSelectedCategory] = useState<MenuCategory | 'all'>('all');

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startDate: Date | undefined;

      switch (dateRange) {
        case '3m':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case '6m':
          startDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
          break;
        case '1y':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = undefined;
      }

      const params = startDate
        ? {
            startDate: startDate.toISOString().split('T')[0],
            endDate: now.toISOString().split('T')[0],
          }
        : undefined;

      const data = await advancedAnalyticsApi.getMenuEngineering(params);
      setMenuData(data);
    } catch (error) {
      console.error('Failed to load menu engineering:', error);
      alert('Errore nel caricamento del Menu Engineering');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-400">Caricamento Menu Engineering...</p>
        </div>
      </div>
    );
  }

  if (!menuData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
        <div className="text-center text-gray-500 dark:text-slate-400">
          <p>Nessun dato disponibile</p>
        </div>
      </div>
    );
  }

  const allItems = [
    ...menuData.stars,
    ...menuData.plowhorses,
    ...menuData.puzzles,
    ...menuData.dogs,
  ];

  const filteredItems =
    selectedCategory === 'all'
      ? allItems
      : allItems.filter((item) => item.menuCategory === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 transition-colors duration-300">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl border-b border-gray-200 dark:border-slate-700/50 shadow-sm dark:shadow-xl dark:shadow-slate-950/50">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white dark:drop-shadow-[0_2px_10px_rgba(255,255,255,0.15)]">Menu Engineering</h1>
              <p className="text-gray-600 dark:text-slate-400 mt-1">Analisi scientifica della performance del menù</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as any)}
                className="px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
              >
                <option value="3m">Ultimi 3 mesi</option>
                <option value="6m">Ultimi 6 mesi</option>
                <option value="1y">Ultimo anno</option>
                <option value="all">Tutto il periodo</option>
              </select>
              <button
                onClick={loadData}
                className="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 shadow-lg dark:shadow-indigo-900/50 transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Aggiorna
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Key Metrics */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Panoramica Menu</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Piatti Totali"
              value={menuData.totalMenuItems.toString()}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              }
              color="blue"
              subtitle="Totale piatti analizzati"
            />
            <MetricCard
              title="Stars"
              value={menuData.stars.length.toString()}
              icon={<span className="text-2xl">⭐</span>}
              color="green"
              subtitle="Alta popolarità & profitto"
            />
            <MetricCard
              title="Plowhorses"
              value={menuData.plowhorses.length.toString()}
              icon={<span className="text-2xl">🐴</span>}
              color="amber"
              subtitle="Alta popolarità, basso profitto"
            />
            <MetricCard
              title="Puzzles"
              value={menuData.puzzles.length.toString()}
              icon={<span className="text-2xl">🧩</span>}
              color="purple"
              subtitle="Bassa popolarità, alto profitto"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <MetricCard
              title="Dogs"
              value={menuData.dogs.length.toString()}
              icon={<span className="text-2xl">🐕</span>}
              color="red"
              subtitle="Bassa popolarità & profitto"
            />
            <MetricCard
              title="Margine Medio"
              value={`€${menuData.averageContributionMargin.toFixed(2)}`}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              color="indigo"
              subtitle="Margine di contribuzione medio"
            />
            <MetricCard
              title="Popolarità Media"
              value={`${menuData.averagePopularityIndex.toFixed(1)}%`}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              }
              color="blue"
              subtitle="Indice di popolarità medio"
            />
          </div>
        </div>

        {/* Matrix Visualization */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-sm dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-6 transition-all duration-300"
        >
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Menu Engineering Matrix</h2>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Classificazione 2x2 basata su popolarità e profitto
            </p>
          </div>
          <MenuEngineeringMatrix
            items={allItems}
            avgPopularity={menuData.averagePopularityIndex}
            avgMargin={menuData.averageContributionMargin}
          />
        </motion.div>

        {/* Detailed Items Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-sm dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-6 transition-all duration-300"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Dettaglio Piatti</h2>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as any)}
              className="px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
            >
              <option value="all">Tutti i piatti ({allItems.length})</option>
              <option value={MenuCategory.STAR}>
                {MENU_CATEGORY_LABELS[MenuCategory.STAR]} ({menuData.stars.length})
              </option>
              <option value={MenuCategory.PLOWHORSE}>
                {MENU_CATEGORY_LABELS[MenuCategory.PLOWHORSE]} ({menuData.plowhorses.length})
              </option>
              <option value={MenuCategory.PUZZLE}>
                {MENU_CATEGORY_LABELS[MenuCategory.PUZZLE]} ({menuData.puzzles.length})
              </option>
              <option value={MenuCategory.DOG}>
                {MENU_CATEGORY_LABELS[MenuCategory.DOG]} ({menuData.dogs.length})
              </option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Piatto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Venduti
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Popolarità
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Margine
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Profitto Tot.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Raccomandazione
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-900/30 divide-y divide-gray-200 dark:divide-slate-700/50">
                {filteredItems.map((item) => (
                  <tr key={item.productId} className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {item.imageUrl && (
                          <img
                            src={item.imageUrl}
                            alt={item.productName}
                            className="h-10 w-10 rounded-lg object-cover mr-3 border border-gray-200 dark:border-slate-600"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{item.productName}</div>
                          <div className="text-xs text-gray-500 dark:text-slate-400">{item.categoryName}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white">
                        {MENU_CATEGORY_LABELS[item.menuCategory]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-bold text-gray-900 dark:text-white">{item.quantitySold}</div>
                      <div className="text-xs text-gray-500 dark:text-slate-400">#{item.popularityRank}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-bold text-gray-900 dark:text-white">
                        {item.popularityIndex.toFixed(1)}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-bold text-gray-900 dark:text-white">
                        €{item.contributionMargin.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-slate-400">
                        {item.contributionMarginRatio.toFixed(0)}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm font-bold text-green-600 dark:text-green-400">
                        €{item.totalProfit.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-gray-700 dark:text-slate-300">{item.recommendation}</div>
                      {item.suggestedPrice && (
                        <div className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-1">
                          Prezzo suggerito: €{item.suggestedPrice.toFixed(2)} (attuale: €
                          {item.unitPrice.toFixed(2)})
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Action Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 dark:backdrop-blur-xl rounded-xl shadow-sm dark:shadow-xl dark:shadow-blue-950/50 border border-blue-200 dark:border-blue-700/50 dark:ring-1 dark:ring-blue-500/20 p-6 transition-all duration-300"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            Azioni Consigliate
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {menuData.stars.length > 0 && (
              <div className="bg-white dark:bg-slate-800/70 rounded-lg p-4 border-l-4 border-green-500 dark:border-green-400 dark:shadow-md dark:shadow-green-900/30">
                <p className="font-medium text-green-900 dark:text-green-300 flex items-center gap-2">
                  ⭐ Stars ({menuData.stars.length} piatti)
                </p>
                <p className="text-sm text-gray-700 dark:text-slate-300 mt-1">
                  Mantieni la qualità e promuovi attivamente. Considera di aumentare leggermente il prezzo
                  per massimizzare i profitti senza perdere clienti.
                </p>
              </div>
            )}
            {menuData.plowhorses.length > 0 && (
              <div className="bg-white dark:bg-slate-800/70 rounded-lg p-4 border-l-4 border-amber-500 dark:border-amber-400 dark:shadow-md dark:shadow-amber-900/30">
                <p className="font-medium text-amber-900 dark:text-amber-300 flex items-center gap-2">
                  🐴 Plowhorses ({menuData.plowhorses.length} piatti)
                </p>
                <p className="text-sm text-gray-700 dark:text-slate-300 mt-1">
                  Piatti popolari ma poco profittevoli. Aumenta i prezzi del 5-10% o riduci i costi degli
                  ingredienti. I clienti li comprano già, sopporteranno un piccolo aumento.
                </p>
              </div>
            )}
            {menuData.puzzles.length > 0 && (
              <div className="bg-white dark:bg-slate-800/70 rounded-lg p-4 border-l-4 border-purple-500 dark:border-purple-400 dark:shadow-md dark:shadow-purple-900/30">
                <p className="font-medium text-purple-900 dark:text-purple-300 flex items-center gap-2">
                  🧩 Puzzles ({menuData.puzzles.length} piatti)
                </p>
                <p className="text-sm text-gray-700 dark:text-slate-300 mt-1">
                  Alto margine ma vendite basse. Migliora il marketing: foto migliori, descrizioni
                  accattivanti, posizionamento strategico nel menù, o bundle con altri piatti.
                </p>
              </div>
            )}
            {menuData.dogs.length > 0 && (
              <div className="bg-white dark:bg-slate-800/70 rounded-lg p-4 border-l-4 border-red-500 dark:border-red-400 dark:shadow-md dark:shadow-red-900/30">
                <p className="font-medium text-red-900 dark:text-red-300 flex items-center gap-2">
                  🐕 Dogs ({menuData.dogs.length} piatti)
                </p>
                <p className="text-sm text-gray-700 dark:text-slate-300 mt-1">
                  Vendite basse e margini bassi. Considera di rimuoverli dal menù per semplificare le
                  operazioni in cucina e ridurre lo spreco. Sostituiscili con piatti più profittevoli.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
